import type { AuthSessionState } from "@amoreg/contracts";
import { squashAtomCommandFailure } from "@amoreg/client-runtime/state/runtime";
import { Vault } from "@lucasmarkes/hairline/react";
import React, {
  startTransition,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";

import { SignedOutAnalyticsPreferences } from "../../analytics/AnalyticsConsent";
import { APP_DISPLAY_NAME } from "../../branding";
import { connectPairing } from "../../connection/onboarding";
import {
  peekPairingTokenFromUrl,
  stripPairingTokenFromUrl,
  submitServerAuthCredential,
} from "../../environments/primary";
import { resolvePrimaryEnvironmentHttpUrl } from "../../environments/primary/target";
import { readHostedPairingRequest } from "../../hostedPairing";
import { CheckIcon, KeyRoundIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import { HairlineArt } from "../ui/hairline-art";
import { Input } from "../ui/input";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "../ui/input-otp";
import { Spinner } from "../ui/spinner";
import { AmoregMark } from "../hairline/figures";
import {
  initialSignInFlow,
  isHostedOnlyError,
  looksLikeEmail,
  MAGIC_CODE_LENGTH,
  requestMagicCode,
  resendCooldownRemaining,
  signInFlowReducer,
  singleFlight,
  verifyMagicCode,
} from "./magicAuth";
import { RuledSilk } from "./RuledSilk";
import { useAtomCommand } from "../../state/use-atom-command";

/**
 * The card beside the auth forms: a vault that turns while it waits, standing
 * on ruled silk that ends where the quote begins. The vault's filled plates
 * hide the rules behind it.
 */
function AuthVaultPanel() {
  return (
    <div className="relative hidden min-h-[calc(100svh-3rem)] flex-col overflow-hidden rounded-[1.75rem] bg-card shadow-2xl shadow-black/10 ring-1 ring-black/5 lg:flex dark:shadow-black/60 dark:ring-white/10">
      <div className="relative flex flex-1 items-center justify-center p-10">
        <RuledSilk className="pointer-events-none absolute inset-0 size-full [mask-image:linear-gradient(to_bottom,transparent,black_14%,black_78%,transparent)]" />
        <HairlineArt figure={Vault} surface="card" play className="w-full max-w-md" />
      </div>

      <figure className="p-10 pt-0">
        <blockquote className="max-w-md text-pretty font-medium text-2xl text-foreground leading-snug">
          “Compliance shouldn’t mean reading every regulation twice. It should mean knowing which
          two lines changed.”
        </blockquote>
        <figcaption className="mt-4 text-sm text-muted-foreground">
          Amoreg — regulatory intelligence for financial services
        </figcaption>
      </figure>
    </div>
  );
}

export function PairingPendingSurface() {
  return (
    <div className="grid min-h-svh bg-background lg:grid-cols-[1.05fr_1fr] lg:gap-8 lg:p-6">
      <AuthVaultPanel />
      <div className="flex items-center justify-center bg-background p-6 md:p-12">
        <div className="w-full max-w-[420px] text-center">
          <HairlineArt figure={AmoregMark} className="mx-auto mb-5 w-40" />
          <h1 className="mb-2 font-bold text-3xl tracking-tight">Signing you in…</h1>
          <p className="text-muted-foreground text-sm">
            Validating your session with this workspace.
          </p>
        </div>
      </div>
    </div>
  );
}

export const WORKOS_SESSION_STORAGE_KEY = "amoreg.workos.sessionId";

/**
 * Motion on the sign-in page, all under 300ms and all ease-out: a step
 * slides the way the person is going, the code slots cascade in, an error
 * settles in from above, a press gives back a little. Reduced motion keeps
 * the fades and drops the movement.
 */
const STEP_ENTER = {
  forward:
    "motion-safe:animate-[auth-step-forward_240ms_var(--ease-out-strong)_both] motion-reduce:animate-[auth-fade-in_200ms_ease-out_both]",
  back: "motion-safe:animate-[auth-step-back_240ms_var(--ease-out-strong)_both] motion-reduce:animate-[auth-fade-in_200ms_ease-out_both]",
} as const;
const SLOT_ENTER = "motion-safe:animate-[auth-slot-in_260ms_var(--ease-out-strong)_both]";
const SLOT_STAGGER_MS = 35;
const BANNER_ENTER =
  "motion-safe:animate-[auth-banner-in_200ms_var(--ease-out-strong)_both] motion-reduce:animate-[auth-fade-in_160ms_ease-out_both]";
const SUCCESS_POP = "motion-safe:animate-[auth-success-pop_320ms_var(--ease-out-strong)]";
const PRESS =
  "transition-transform duration-150 ease-out active:scale-[0.98] motion-reduce:active:scale-100";
/**
 * Full-width and as tall as the fields. `sm:h-12` because the kit's `lg`
 * size shrinks to `sm:h-9` from the `sm` breakpoint up, which `h-12` alone
 * does not override — desktop buttons came out a head shorter than the input.
 */
const AUTH_BUTTON = "h-12 w-full sm:h-12";
/** How long the accepted code is shown before the page moves on. */
const SUCCESS_SETTLE_MS = 450;

const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** A refused code shakes the slots — once per refusal, interruptible, no remount. */
const shake = (element: HTMLElement | null) => {
  if (!element || prefersReducedMotion() || typeof element.animate !== "function") return;
  element.animate(
    [
      { transform: "translateX(0)" },
      { transform: "translateX(-7px)" },
      { transform: "translateX(6px)" },
      { transform: "translateX(-4px)" },
      { transform: "translateX(2px)" },
      { transform: "translateX(0)" },
    ],
    { duration: 320, easing: "ease-out" },
  );
};

/**
 * The WorkOS callback appends ?wsid=<AuthKit session id> so sign-out can end
 * the WorkOS session too. Stash it and strip it from the address bar.
 */
function captureWorkosSessionIdFromUrl(): void {
  const url = new URL(window.location.href);
  const wsid = url.searchParams.get("wsid");
  if (!wsid) {
    return;
  }
  try {
    window.localStorage.setItem(WORKOS_SESSION_STORAGE_KEY, wsid);
  } catch {
    // Storage unavailable: sign-out will simply skip the WorkOS hop.
  }
  url.searchParams.delete("wsid");
  window.history.replaceState({}, document.title, url.toString());
}

/** Friendly copy for the `?error=` codes the WorkOS callback redirects with. */
function loginErrorFromUrl(): string {
  const error = new URL(window.location.href).searchParams.get("error");
  switch (error) {
    case "not_a_member":
      return "This account is not a member of this workspace yet. Ask an admin for access.";
    case "login_failed":
      return "Sign-in did not complete. Please try again.";
    default:
      return "";
  }
}

export function PairingRouteSurface({
  initialErrorMessage,
  onAuthenticated,
}: {
  auth: AuthSessionState["auth"];
  initialErrorMessage?: string;
  onAuthenticated: () => void;
}) {
  const autoPairTokenRef = useRef<string | null>(peekPairingTokenFromUrl());
  useEffect(() => {
    captureWorkosSessionIdFromUrl();
  }, []);
  const [errorMessage, setErrorMessage] = useState(
    () => initialErrorMessage ?? loginErrorFromUrl(),
  );
  const [isAutoExchanging, setIsAutoExchanging] = useState(autoPairTokenRef.current !== null);
  const autoSubmitAttemptedRef = useRef(false);
  // The headless sign-in: email → emailed code → pairing credential. The
  // hosted AuthKit page remains for what only it can do (passkeys, SSO).
  const [flow, dispatch] = useReducer(signInFlowReducer, undefined, () => initialSignInFlow());
  const [now, setNow] = useState(() => Date.now());
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const codeFieldRef = useRef<HTMLDivElement>(null);
  // One redemption at a time: see `singleFlight`.
  const [verifyOnce] = useState(() => singleFlight(verifyMagicCode));

  const startWorkosLogin = useCallback(
    (options: { readonly screen?: "sign-up"; readonly loginHint?: string } = {}) => {
      // The hosted route is still an authenticated way to obtain a pairing
      // token: the server callback redirects back to /pair#token=…, which this
      // page exchanges automatically. Passkeys live only there (WorkOS offers
      // no headless passkey API), so the typed address rides along to spare
      // the person retyping it.
      const base = resolvePrimaryEnvironmentHttpUrl("/");
      const start = new URL("/api/amoreg/auth/workos/start", new URL(base).origin);
      start.searchParams.set("return_to", window.location.origin);
      if (options.screen) {
        start.searchParams.set("screen", options.screen);
      }
      const hint = options.loginHint?.trim() ?? "";
      if (looksLikeEmail(hint)) {
        start.searchParams.set("login_hint", hint);
      }
      window.location.assign(start.toString());
    },
    [],
  );

  const submitCredential = useCallback(
    async (nextCredential: string): Promise<boolean> => {
      setErrorMessage("");

      const submitError = await submitServerAuthCredential(nextCredential).then(
        () => null,
        (error) => errorMessageFromUnknown(error),
      );

      if (submitError) {
        setErrorMessage(submitError);
        return false;
      }

      startTransition(() => {
        onAuthenticated();
      });
      return true;
    },
    [onAuthenticated],
  );

  useEffect(() => {
    const token = autoPairTokenRef.current;
    if (!token || autoSubmitAttemptedRef.current) {
      return;
    }

    autoSubmitAttemptedRef.current = true;
    stripPairingTokenFromUrl();
    void submitCredential(token).finally(() => {
      setIsAutoExchanging(false);
    });
  }, [submitCredential]);

  // The resend countdown only ticks while it is showing, and stops itself.
  useEffect(() => {
    if (flow.step !== "code" || flow.resendAfter <= Date.now()) {
      return;
    }
    const timer = window.setInterval(() => {
      const tick = Date.now();
      setNow(tick);
      if (tick >= flow.resendAfter) {
        window.clearInterval(timer);
      }
    }, 1000);
    return () => window.clearInterval(timer);
  }, [flow.step, flow.resendAfter]);

  useEffect(() => {
    if (flow.step === "code") {
      codeInputRef.current?.focus();
    }
  }, [flow.step]);

  useEffect(() => {
    if (flow.failures > 0 && flow.step === "code") {
      shake(codeFieldRef.current);
      codeInputRef.current?.focus();
    }
  }, [flow.failures, flow.step]);

  const sendCode = useCallback(async () => {
    const email = flow.email.trim();
    if (!looksLikeEmail(email)) {
      dispatch({ type: "failed", error: "invalid_email", message: "Enter a valid email address." });
      emailInputRef.current?.focus();
      return;
    }
    // A message carried over from the callback redirect belongs to the last
    // attempt, not this one.
    setErrorMessage("");
    dispatch({ type: "request-started" });
    const result = await requestMagicCode(email);
    if (result.ok) {
      dispatch({ type: "code-sent", state: result.state, now: Date.now() });
      setNow(Date.now());
    } else if (isHostedOnlyError(result.error)) {
      // A server without the headless routes (an older deploy, a rollout in
      // progress) still signs people in the way it always has.
      startWorkosLogin({ loginHint: email });
    } else {
      dispatch({ type: "failed", error: result.error, message: result.message });
    }
  }, [flow.email, startWorkosLogin]);

  // `submitted` is the value the OTP field reports on completion: its change
  // and completion fire in one event, before this closure sees the new code.
  const redeemCode = useCallback(
    async (submitted?: string) => {
      // A second completion during the success settle (a digit retyped) must
      // not resubmit the already-redeemed code.
      if (flow.succeeded) return;
      const code = submitted ?? flow.code;
      if (code.length !== MAGIC_CODE_LENGTH) {
        dispatch({
          type: "failed",
          error: "invalid_request",
          message: "Enter the six-digit code from the email.",
        });
        codeInputRef.current?.focus();
        return;
      }
      const pending = verifyOnce({
        email: flow.email.trim(),
        code,
        state: flow.state,
      });
      dispatch({ type: "verify-started" });
      const result = await pending;
      // A second submission while the first is in flight: that one decides.
      if (result === null) return;
      if (!result.ok) {
        dispatch({ type: "failed", error: result.error, message: result.message });
        // This deployment serves none of the person's organizations: go to the
        // one that does and sign in there (sessions are per host).
        if (result.error === "served_elsewhere" && result.url) {
          window.location.assign(result.url);
        }
        return;
      }
      if (result.workosSessionId) {
        try {
          window.localStorage.setItem(WORKOS_SESSION_STORAGE_KEY, result.workosSessionId);
        } catch {
          // Storage unavailable: sign-out will simply skip the WorkOS hop.
        }
      }
      // Let the accepted code be seen before the page moves on.
      dispatch({ type: "succeeded" });
      if (!prefersReducedMotion()) {
        await new Promise((resolve) => window.setTimeout(resolve, SUCCESS_SETTLE_MS));
      }
      // From here on it is the same exchange the callback's #token takes.
      setIsAutoExchanging(true);
      const exchanged = await submitCredential(result.credential).finally(() =>
        setIsAutoExchanging(false),
      );
      if (!exchanged) {
        // The one-time credential is spent; the banner keeps the exchange's
        // message and the form opens again for a fresh code.
        dispatch({ type: "change-email" });
      }
    },
    [flow.code, flow.email, flow.state, flow.succeeded, submitCredential, verifyOnce],
  );

  const resendRemaining = resendCooldownRemaining(flow, now);
  const slotClassName = cn(
    // The six boxes span the same width as the field and button around them.
    "w-auto flex-1",
    SLOT_ENTER,
    flow.succeeded && "border-emerald-500/80 text-emerald-600 dark:text-emerald-400",
  );

  return (
    <div className="grid min-h-svh bg-background lg:grid-cols-[1.05fr_1fr] lg:gap-8 lg:p-6">
      <AuthVaultPanel />

      <div className="flex items-center justify-center bg-background p-6 md:p-12">
        <div className="w-full max-w-[420px]">
          <div className="mb-10 text-center">
            <a aria-label="Amoreg home" className="mb-5 inline-flex items-center" href="/">
              <HairlineArt figure={AmoregMark} className="w-40" />
            </a>
            <div
              className={STEP_ENTER[flow.direction]}
              key={isAutoExchanging ? "exchange" : flow.step}
            >
              <h1 className="mb-2 font-bold text-3xl tracking-tight">
                {isAutoExchanging
                  ? "Signing you in…"
                  : flow.step === "code"
                    ? "Check your email"
                    : "Welcome back!"}
              </h1>
              <p className="text-muted-foreground text-sm">
                {isAutoExchanging
                  ? "Finishing authentication with this workspace."
                  : flow.step === "code"
                    ? `We sent a ${MAGIC_CODE_LENGTH}-digit code to ${flow.email.trim()}.`
                    : "Sign in to your compliance workspace"}
              </p>
            </div>
          </div>

          {isAutoExchanging ? null : (
            <>
              {errorMessage || flow.error ? (
                <div
                  className={cn(
                    "mb-5 rounded-lg border border-destructive/30 bg-destructive/6 px-3 py-2 text-destructive text-sm",
                    BANNER_ENTER,
                  )}
                  key={flow.error || errorMessage}
                  role="alert"
                >
                  {flow.error || errorMessage}
                  {flow.hostedFallback ? (
                    <>
                      {" "}
                      <button
                        className="font-medium underline underline-offset-2"
                        onClick={() => startWorkosLogin({ loginHint: flow.email })}
                        type="button"
                      >
                        Continue on the full sign-in page
                      </button>
                    </>
                  ) : null}
                </div>
              ) : null}

              <div className={STEP_ENTER[flow.direction]} key={flow.step}>
                {flow.step === "email" ? (
                  <form
                    className="space-y-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void sendCode();
                    }}
                  >
                    <label className="sr-only" htmlFor="sign-in-email">
                      Email address
                    </label>
                    <Input
                      aria-invalid={flow.error ? true : undefined}
                      autoComplete="email"
                      autoFocus
                      className="h-12 text-base [&>input]:h-auto"
                      disabled={flow.busy}
                      id="sign-in-email"
                      inputMode="email"
                      name="email"
                      onChange={(event) => {
                        // Typing a new address retires the callback's message too.
                        setErrorMessage("");
                        dispatch({ type: "email-changed", email: event.target.value });
                      }}
                      placeholder="name@company.com"
                      ref={emailInputRef}
                      required
                      type="email"
                      value={flow.email}
                    />
                    <Button
                      className={cn(AUTH_BUTTON, PRESS)}
                      disabled={flow.busy}
                      size="lg"
                      type="submit"
                    >
                      {flow.busy ? (
                        <>
                          <Spinner className="size-4" />
                          Sending code…
                        </>
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </form>
                ) : (
                  <form
                    className="space-y-3"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void redeemCode();
                    }}
                  >
                    <label className="sr-only" htmlFor="sign-in-code">
                      One-time code
                    </label>
                    <div
                      // Accepted: frozen by pointer-events, not disabled, so the
                      // green reads at full strength instead of the dimmed state.
                      className={cn(
                        "flex justify-center",
                        flow.succeeded && cn(SUCCESS_POP, "pointer-events-none"),
                      )}
                      ref={codeFieldRef}
                    >
                      <InputOTP
                        aria-invalid={flow.error ? true : undefined}
                        autoComplete="one-time-code"
                        containerClassName="w-full"
                        disabled={flow.busy}
                        id="sign-in-code"
                        inputMode="numeric"
                        maxLength={MAGIC_CODE_LENGTH}
                        name="code"
                        onChange={(code) => dispatch({ type: "code-changed", code })}
                        // A full code submits itself; the button stays for autofill oddities.
                        onComplete={(code) => void redeemCode(code)}
                        pattern="^[0-9]*$"
                        ref={codeInputRef}
                        value={flow.code}
                      >
                        <InputOTPGroup className="flex-1">
                          <InputOTPSlot
                            aria-invalid={flow.error ? true : undefined}
                            className={slotClassName}
                            index={0}
                            style={{ animationDelay: `${0 * SLOT_STAGGER_MS}ms` }}
                          />
                          <InputOTPSlot
                            aria-invalid={flow.error ? true : undefined}
                            className={slotClassName}
                            index={1}
                            style={{ animationDelay: `${1 * SLOT_STAGGER_MS}ms` }}
                          />
                          <InputOTPSlot
                            aria-invalid={flow.error ? true : undefined}
                            className={slotClassName}
                            index={2}
                            style={{ animationDelay: `${2 * SLOT_STAGGER_MS}ms` }}
                          />
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup className="flex-1">
                          <InputOTPSlot
                            aria-invalid={flow.error ? true : undefined}
                            className={slotClassName}
                            index={3}
                            style={{ animationDelay: `${3 * SLOT_STAGGER_MS}ms` }}
                          />
                          <InputOTPSlot
                            aria-invalid={flow.error ? true : undefined}
                            className={slotClassName}
                            index={4}
                            style={{ animationDelay: `${4 * SLOT_STAGGER_MS}ms` }}
                          />
                          <InputOTPSlot
                            aria-invalid={flow.error ? true : undefined}
                            className={slotClassName}
                            index={5}
                            style={{ animationDelay: `${5 * SLOT_STAGGER_MS}ms` }}
                          />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    <Button
                      className={cn(AUTH_BUTTON, PRESS)}
                      disabled={flow.busy || flow.succeeded}
                      size="lg"
                      type="submit"
                    >
                      {flow.succeeded ? (
                        <>
                          <CheckIcon />
                          Signed in
                        </>
                      ) : flow.busy ? (
                        <>
                          <Spinner className="size-4" />
                          Signing in…
                        </>
                      ) : (
                        "Sign in"
                      )}
                    </Button>
                    <div className="flex items-center justify-between text-muted-foreground text-sm tabular-nums">
                      <button
                        className="hover:text-foreground hover:underline disabled:hover:no-underline"
                        disabled={flow.busy || flow.succeeded}
                        onClick={() => dispatch({ type: "change-email" })}
                        type="button"
                      >
                        Use a different email
                      </button>
                      <button
                        className="hover:text-foreground hover:underline disabled:hover:no-underline"
                        disabled={flow.busy || flow.succeeded || resendRemaining > 0}
                        onClick={() => void sendCode()}
                        type="button"
                      >
                        {resendRemaining > 0 ? `Resend code in ${resendRemaining}s` : "Resend code"}
                      </button>
                    </div>
                  </form>
                )}

                {flow.step === "email" ? (
                  <>
                    <div
                      aria-hidden="true"
                      className="my-5 flex items-center gap-3 text-muted-foreground text-xs uppercase tracking-[0.14em]"
                    >
                      <span className="h-px flex-1 bg-border" />
                      or
                      <span className="h-px flex-1 bg-border" />
                    </div>
                    {/* Passkeys are hosted-UI only at WorkOS: this hop lands on
                        AuthKit with the address pre-filled, one tap from the key. */}
                    <Button
                      className={cn(AUTH_BUTTON, PRESS)}
                      disabled={flow.busy}
                      onClick={() => startWorkosLogin({ loginHint: flow.email })}
                      size="lg"
                      type="button"
                      variant="outline"
                    >
                      <KeyRoundIcon />
                      Sign in with a passkey
                    </Button>
                    <p className="mt-8 text-center text-muted-foreground text-sm">
                      New to Amoreg? The same code signs you up.
                    </p>
                  </>
                ) : null}
              </div>
              <SignedOutAnalyticsPreferences />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function HostedPairingRouteSurface() {
  const connectPairingEnvironment = useAtomCommand(connectPairing, {
    reportFailure: false,
  });
  const hostedPairingRequestRef = useRef(readHostedPairingRequest());
  const [status, setStatus] = useState<"pairing" | "paired" | "error">(() =>
    hostedPairingRequestRef.current ? "pairing" : "error",
  );
  const [message, setMessage] = useState(() =>
    hostedPairingRequestRef.current
      ? "Connecting to this backend."
      : "This pairing link is missing its backend host or token.",
  );
  const [canRetry, setCanRetry] = useState(false);
  const submitAttemptedRef = useRef(false);
  const tokenSubmittedRef = useRef(false);

  const submitHostedPairingRequest = useCallback(async () => {
    const request = hostedPairingRequestRef.current;

    if (!request) {
      setStatus("error");
      setMessage("This pairing link is missing its backend host or token.");
      setCanRetry(false);
      return;
    }

    if (tokenSubmittedRef.current) {
      setStatus("error");
      setMessage("This one-time pairing token was already submitted. Request a new pairing link.");
      setCanRetry(false);
      return;
    }

    setStatus("pairing");
    setMessage("Connecting to this backend.");
    setCanRetry(false);
    tokenSubmittedRef.current = true;

    const result = await connectPairingEnvironment({
      host: request.host,
      pairingCode: request.token,
    });
    if (result._tag === "Success") {
      setStatus("paired");
      setMessage(`${request.label || "The environment"} is saved in this browser.`);
      return;
    }

    tokenSubmittedRef.current = false;
    setStatus("error");
    setCanRetry(true);
    setMessage(
      `${errorMessageFromUnknown(squashAtomCommandFailure(result))} If the backend accepted this one-time token, request a new pairing link before retrying.`,
    );
  }, [connectPairingEnvironment]);

  useEffect(() => {
    if (submitAttemptedRef.current) {
      return;
    }
    submitAttemptedRef.current = true;

    stripPairingTokenFromUrl();
    void submitHostedPairingRequest();
  }, [submitHostedPairingRequest]);

  const request = hostedPairingRequestRef.current;

  return (
    <div className="grid min-h-svh bg-background lg:grid-cols-[1.05fr_1fr] lg:gap-8 lg:p-6">
      <AuthVaultPanel />

      <div className="flex items-center justify-center bg-background p-6 md:p-12">
        <div className="w-full max-w-[420px]">
          <div className="text-center">
            <HairlineArt figure={AmoregMark} className="mx-auto mb-5 w-40" />
            <h1 className="mb-2 font-bold text-3xl tracking-tight">
              {status === "paired"
                ? "Backend paired"
                : status === "error"
                  ? "Pairing failed"
                  : "Pairing backend"}
            </h1>
            <p className="text-muted-foreground text-sm">{message}</p>
          </div>

          {request ? (
            <div className="mt-6 rounded-lg border border-border/70 bg-background/55 px-3 py-3 text-muted-foreground text-xs leading-relaxed">
              Host: <span className="font-mono text-foreground/80">{request.host}</span>
            </div>
          ) : null}

          {status === "error" ? (
            <div className="mt-5 rounded-lg border border-destructive/30 bg-destructive/6 px-3 py-2 text-destructive text-sm">
              Verify the backend is reachable from this browser, supports CORS for hosted clients,
              and is served over HTTPS when opening this page from HTTPS.
            </div>
          ) : null}

          <div className="mt-6 flex justify-center gap-2">
            {status === "pairing" ? (
              <Button disabled size="sm">
                Pairing...
              </Button>
            ) : canRetry ? (
              <Button size="sm" onClick={() => void submitHostedPairingRequest()}>
                Try again
              </Button>
            ) : null}
            {status === "paired" ? (
              <Button size="sm" variant="outline" onClick={() => (window.location.href = "/")}>
                Open app
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function errorMessageFromUnknown(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return "Authentication failed.";
}
